import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import React, { FunctionComponent, memo } from 'react';
import Loading from '../eliot-components/src/Loading/Loading';
import { useManageContext } from '../contexts/ManageContext/ManageContext';
import Visible from '../components/Visible/Visible';
import SearchResults from './components/SearchResults/SearchResults';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../eliot-components/src/utils/helpers';
import SessionTimeout from '../eliot-components/src/SessionTimeout';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexGrow: 1,
    paddingTop: theme.mixins.toolbar.minHeight,
    [theme.breakpoints.up('xs')]: {
      paddingTop: 56,
    },
    [theme.breakpoints.up('sm')]: {
      paddingTop: 64,
    },
  },
  mainContentContainer: {
    flexGrow: 1,
    padding: theme.spacing(2),
    maxWidth: '100%',
    transition: 'width 500ms',
    display: 'flex',
    justifyContent: 'center',
    '& .mainContent': {
      flexGrow: 1,
      maxWidth: theme.breakpoints.values.lg,
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
      },
    },
  },
  bottomRightAction: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
}));

interface Props {
  readonly isLoading?: boolean;
  readonly error?: FetchBaseQueryError | SerializedError | Error | null;
  nullifyError?: () => void;
  mainContentRef?: React.RefObject<HTMLDivElement>;
  bottomRightAction?: JSX.Element;
}

const Layout: FunctionComponent<Props> = memo(
  ({
    isLoading = false,
    error = null,
    children,
    mainContentRef,
    bottomRightAction,
  }) => {
    const classes = useStyles();
    const { state, dispatch } = useManageContext();
    const { enqueueSnackbar } = useSnackbar();

    const resultsVisible =
      (state.isAppSearchVisible &&
        state.searchQuery &&
        state.searchQuery.length > 0) ||
      (!!state.advancedSearchFilters && state.isAppSearchVisible);

    return (
      <>
        <SessionTimeout />
        <main className={classes.mainContentContainer}>
          <div className="mainContent" ref={mainContentRef}>
            <Visible when={resultsVisible}>
              <SearchResults />
            </Visible>

            <Visible when={!resultsVisible}>
              {(() => {
                if (error) {
                  enqueueSnackbar(parseErrorMessage(error), {
                    variant: 'error',
                  });
                  return <Loading />;
                }
                if (isLoading) {
                  return <Loading />;
                }
                return (
                  <div
                    onClick={() => {
                      if (state.isAppSearchVisible) {
                        dispatch({
                          type: 'search-closed',
                        });
                      }
                    }}
                  >
                    {children}
                  </div>
                );
              })()}
            </Visible>
          </div>
          <div className={classes.bottomRightAction}>{bottomRightAction}</div>
        </main>
      </>
    );
  }
);

export default Layout;
